'use client';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { CardContent } from '@startuptools/ui/CardContent';
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@startuptools/shad-ui/chart';
import { Trans, useLingui } from '@lingui/react';
import React from 'react';
import { Typography } from '@startuptools/ui/Typography';
import { Switch } from '@startuptools/ui/Switch';
import { Alert } from '@startuptools/ui/Alert';
import { formatNumber } from '@startuptools/ui/NumberFormat';
import { formatDate } from '@startuptools/ui/format-date';
import { BlurOverlay } from '@startuptools/ui/BlurOverlay';
import { DateTime } from 'luxon';

const exampleData = [
  {
    registrationDate: '2014-05-06',
    sharePrice: '10',
    valuation: '100000',
  },
  {
    registrationDate: '2014-07-01',
    sharePrice: '20',
    valuation: '52000000',
  },
  {
    registrationDate: '2015-05-27',
    sharePrice: '100',
    valuation: '624000000',
  },
];

export function ValuationChart({
  data,
  currency,
}: {
  data?: {
    registrationDate: string;
    valuation: number;
    sharePrice: number;
  }[];
  currency?: string | null | undefined;
}) {
  const { i18n } = useLingui();

  const hideValuationGraph = !data || data.length <= 1;

  const chartConfig = {
    valuation: {
      label: <Trans id="Valuation" />,
      color: 'hsl(var(--chart-1))',
    },
    sharePrice: {
      label: <Trans id="Share price" />,
    },
  } satisfies ChartConfig;

  const [dataKey, setDataKey] = React.useState<'valuation' | 'sharePrice'>('valuation');

  const usedData = hideValuationGraph
    ? exampleData
    : [...data, { ...data[data.length - 1], registrationDate: DateTime.local().toISODate() }];

  return (
    <>
      <Alert>
        {hideValuationGraph ? (
          <Trans id="No share issues have been registered with Bolagsverket. When they have, you can follow your valuation here." />
        ) : (
          <Trans id="Based on share issue data from Bolagsverket." />
        )}
      </Alert>
      <Switch
        sx={{ m: 2 }}
        color="primary"
        startDecorator={
          <Typography>
            <Trans id="Share price" />
          </Typography>
        }
        endDecorator={<Trans id="Valuation" />}
        checked={dataKey === 'valuation'}
        onChange={event => setDataKey(event.target.checked ? 'valuation' : 'sharePrice')}
      />

      <CardContent>
        <BlurOverlay show={hideValuationGraph}>
          <ChartContainer config={chartConfig} className="min-h-[200px] w-full">
            <AreaChart
              data={usedData}
              margin={{
                left: 72,
                right: 12,
                top: 20,
                bottom: 20,
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="registrationDate"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={value =>
                  formatDate(value, { locale: i18n.locale, format: { month: 'short', year: 'numeric' } }) || ''
                }
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={value =>
                  formatNumber(value, {
                    locale: i18n.locale,
                    variant: 'currency',
                    currency: currency || 'SEK',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }) || ''
                }
              />
              <YAxis yAxisId="right" orientation="right" tickLine={false} axisLine={false} tickMargin={8} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={value => {
                      return formatDate(value, { locale: i18n.locale, format: { month: 'short', year: 'numeric' } });
                    }}
                  />
                }
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey={dataKey}
                name={dataKey}
                stroke="var(--color-valuation)"
                fill="var(--color-valuation)"
                fillOpacity={0.4}
              />
            </AreaChart>
          </ChartContainer>
        </BlurOverlay>
      </CardContent>
    </>
  );
}
